import { Col, Input, Row } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { translate } from '@portal/services/i18n';

import { useDispatch } from 'react-redux';
import CollapsibleTable from '@portal/components/CollapsibleTable/CollapsibleTable';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import Header from '@portal/components/Header/Header';
import { editGroup, getGroupForId } from '@portal/actions/group';
import useReduxState from '@portal/hooks/useReduxState';

import { getRouteStackPath } from '@portal/config/routes';
import { IS_EDIT, UNSET_EDIT_GROUP } from '@portal/actions/actionTypes';
import { ListGroup } from '@portal/models/reducers';
import { availablePermissions } from '@portal/actions/user';
import { useNavigation } from '@portal/hooks/useNavigation';

interface RowRemit {
  minLimit: number;
  maxLimit: number;
  approvers: ListGroup[];
  minApprovers: number;
}
interface Remit {
  rows: RowRemit[];
}
interface History {
  item: string;
  uid: string;
  active: boolean;
  remit?: Remit;
}
interface Data {
  name: string;
  active: boolean;
  history: History[];
}

const EditProfileManagerList = () => {
  const [onFocusSearch, setOnFocusSearch] = useState(false);
  const [searchText, setSeachText] = useState<string>('');
  const [groupId, setGroupId] = useState<string>('');
  const [rows, setRows] = React.useState<Data[]>([]);

  const dispatch = useDispatch();

  const navigate = useNavigation<{ groupId?: string }>();

  const {
    group: { hasEditGroup, showGroup },
    remit,
  } = useReduxState();

  const {
    user: { permissionsList },
  } = useReduxState();

  useEffect(() => {
    setGroupId(navigate?.location?.state?.groupId ?? '');
  }, [navigate?.location?.state?.groupId]);

  useEffect(() => {
    dispatch({
      type: IS_EDIT,
      payload: true,
    });
  }, [dispatch]);

  useEffect(() => {
    if (groupId) {
      dispatch(getGroupForId(groupId));
    }
  }, [groupId, dispatch]);

  const rowPayment = React.useMemo(
    () => [
      {
        uid: 'transfer',
        item: translate('PAGES.PROFILE_MANAGER.REMIT.TRANSFER'),
        active: !!(
          showGroup?.accountTransactionRules?.transfer &&
          showGroup?.accountTransactionRules?.transfer?.length > 0
        ),
      },
      {
        uid: 'pix',
        item: translate('PAGES.PROFILE_MANAGER.REMIT.PIX'),
        active: !!(
          showGroup?.accountTransactionRules?.pix &&
          showGroup?.accountTransactionRules?.pix?.length > 0
        ),
      },
      {
        uid: 'billet',
        item: translate('PAGES.PROFILE_MANAGER.REMIT.BILLET'),
        active: !!(
          showGroup?.accountTransactionRules?.billet &&
          showGroup?.accountTransactionRules?.billet?.length > 0
        ),
      },
    ],
    [showGroup?.accountTransactionRules]
  );

  useEffect(() => {
    dispatch(availablePermissions());
  }, [dispatch]);

  const rowsMemo = React.useMemo((): History[] => {
    const data: History[] = [];

    permissionsList.forEach((itemPermission) => {
      const isActive = showGroup?.permissions.some(
        (item) => item.value === itemPermission.value
      );

      data.push({
        active: !!isActive,
        item: itemPermission.name ?? '',
        uid: itemPermission.value ?? '',
      });
    });

    return data;
  }, [permissionsList, showGroup]);

  const mountRoles = useCallback(() => {
    const result: string[] = [];
    rows.forEach((item) => {
      if (item.active) {
        return item.history.forEach((item) => {
          if (item.active) {
            result.push(item.uid);
          }
        });
      }
    });

    const permissionServices =
      showGroup?.permissions &&
      showGroup?.permissions.map((item) => {
        return item.value;
      }, []);

    if (JSON.stringify(permissionServices) === JSON.stringify(result))
      return null;
    else return result;
  }, [rows, showGroup]);

  useEffect(() => {
    const posRows: Data[] = [
      {
        name: translate('PAGES.PROFILE_MANAGER.SERVICES'),
        active: !!(showGroup?.permissions && showGroup.permissions.length > 0),
        history: rowsMemo ?? [],
      },
      {
        name: translate('PAGES.PROFILE_MANAGER.PAYMENT'),
        history: rowPayment,
        active: false,
      },
    ];

    setRows(posRows);
  }, [rowPayment, rowsMemo, showGroup]);

  useEffect(() => {
    setSeachText(showGroup?.name ?? '');
  }, [showGroup?.name]);

  useEffect(() => {
    if (hasEditGroup) {
      setTimeout(() => {
        dispatch({
          type: UNSET_EDIT_GROUP,
        });
        navigate(getRouteStackPath('PROFILE_MANAGER', 'PROFILE_LIST'));
      }, 2000);
    }
  }, [hasEditGroup, dispatch, navigate]);

  return (
    <form>
      <Col className="create-profile">
        <Header
          subtitle={translate(
            'PAGES.PROFILE_MANAGER.CREATE_PROFILE_SCREEN.TITLE'
          )}
        />

        <Row>
          <label
            className="create-profile__search__label"
            htmlFor="input-search"
          >
            <Input
              id="input-search"
              value={searchText}
              onChange={(e) => setSeachText(e.target.value)}
              onFocus={() => setOnFocusSearch(true)}
              onBlur={() => setOnFocusSearch(false)}
              className="create-profile__search__input"
            />

            {!onFocusSearch && !searchText && (
              <span className="create-profile__search__input-placeholder">
                {translate(
                  'PAGES.PROFILE_MANAGER.CREATE_PROFILE_SCREEN.NAME_OF_GROUP'
                )}
              </span>
            )}
          </label>
        </Row>

        <div>
          <CollapsibleTable rows={rows} setRows={setRows} />
        </div>

        <Row>
          <Col flex={1} />
          <Col style={{ marginTop: 189, marginBottom: 20 }}>
            <AdvancedButton
              height="3.438rem"
              fullWidth={true}
              type="button"
              onClick={() =>
                dispatch(editGroup(searchText, mountRoles(), groupId, remit))
              }
              text={translate('PAGES.USER_MANAGER.CREATE_USER_SCREEN.SAVE_BTN')}
            />
          </Col>
        </Row>
      </Col>
    </form>
  );
};

export default EditProfileManagerList;
